import { useState, useRef, useCallback, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  MapPin, Users, Activity as ActivityIcon, Gauge,
  ArrowLeft, ArrowRight, Check, Mic, Square, Trash2, Loader2,
  Home as HomeIcon, Building2, TreePine, Beer, ShoppingBag as ShoppingBagIcon, Car,
  User as UserIcon, UsersRound, Heart, Users2, UserCog, Crown, Stethoscope, Briefcase,
  Laptop, BookOpen, Coffee, MonitorPlay, PartyPopper, Dumbbell, Sparkles, Brush, Palette, Gamepad2, HandHeart, ShoppingBag, CircleDot,
  AlertCircle, ChevronDown, ChevronUp
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { playSaveChime, playMicStart, playMicStop, playError } from "@/lib/sound";

// ===== CATEGORIES (must match backend voice_mood.py v2.2 — 2026-05-18) =====
// Each item: id (backend value), label, icon, examples (shown on-tap only).
const LOCATIONS = [
  { id: "casa_mia", label: "Casa mia", icon: HomeIcon, examples: "camera · cucina · sala" },
  { id: "casa_altri", label: "Casa di altri", icon: Heart, examples: "da amici · da parenti · da partner" },
  { id: "lavoro_scuola", label: "Lavoro / Aula", icon: Building2, examples: "ufficio · università · biblioteca · coworking" },
  { id: "all_aperto", label: "All'aperto", icon: TreePine, examples: "parco · mare · montagna · passeggiata · giardino" },
  { id: "locali_svago", label: "Locali / Svago", icon: Beer, examples: "bar · ristorante · festa · pub · palestra · cinema" },
  { id: "negozi_servizi", label: "Negozi / Servizi", icon: ShoppingBagIcon, examples: "supermercato · posta · banca · ospedale" },
  { id: "mi_sposto", label: "Mi sto spostando", icon: Car, examples: "a piedi · auto · treno · bus · metro · tram" },
  { id: "altro", label: "Altro", icon: CircleDot, examples: "" },
];

// 2026-05-18 v2.2: partner separato da famiglia, aggiunti superiori e clienti.
// L'identità del "con chi" si combina col flag virtual_interaction (online/telefono).
const SOCIAL = [
  { id: "solo", label: "Solo", icon: UserIcon, examples: "nessuna interazione" },
  { id: "amici", label: "Amici", icon: UsersRound, examples: "" },
  { id: "partner", label: "Partner", icon: Heart, examples: "" },
  { id: "famiglia", label: "Famiglia", icon: Users2, examples: "genitori · figli · fratelli · parenti" },
  { id: "colleghi", label: "Colleghi / Compagni", icon: Briefcase, examples: "" },
  { id: "superiori", label: "Responsabili / Superiori", icon: Crown, examples: "capo · supervisor · docente" },
  { id: "clienti", label: "Clienti / Pazienti", icon: Stethoscope, examples: "utenti · partecipanti" },
  { id: "altri", label: "Altri", icon: UserCog, examples: "" },
];

// 2026-05-18 v2.2: aggiunte cura_casa, cura_altri, svago_online, creativita_hobby.
// 2026-05-21 v2.3: 'gioco' reintrodotto come categoria a sé (separato da creatività/hobby).
const ACTIVITIES = [
  { id: "lavoro", label: "Lavoro", icon: Laptop, examples: "" },
  { id: "studio", label: "Studio", icon: BookOpen, examples: "" },
  { id: "riposo", label: "Riposo", icon: Coffee, examples: "pausa · letto · dormiveglia" },
  { id: "svago_online", label: "Svago online", icon: MonitorPlay, examples: "social · video · streaming · navigare" },
  { id: "vita_sociale", label: "Vita sociale", icon: PartyPopper, examples: "" },
  { id: "sport", label: "Sport", icon: Dumbbell, examples: "" },
  { id: "cura_personale", label: "Cura personale", icon: Sparkles, examples: "igiene · meditazione · psicoterapia" },
  { id: "cura_casa", label: "Cura della casa", icon: Brush, examples: "pulizie · spesa · cucinare · ordine" },
  { id: "creativita_hobby", label: "Creatività / Hobby", icon: Palette, examples: "leggere · scrivere · musica · disegno · cucinare per piacere · progetti personali" },
  { id: "gioco", label: "Gioco", icon: Gamepad2, examples: "videogioco · giochi da tavolo · scacchi · gioco online · enigmistica" },
  { id: "cura_altri", label: "Cura degli altri", icon: HandHeart, examples: "figli · familiari · supporto emotivo · accompagnare qualcuno · aiutare praticamente" },
  { id: "commissioni", label: "Commissioni", icon: ShoppingBag, examples: "pratiche · burocrazia · appuntamenti pratici" },
  { id: "altro", label: "Altro", icon: CircleDot, examples: "" },
];

// EMA dimensions — 2026-05-18 v2.2: mixed granularity (3-5-3-3) by methodological design.
// Pleasantness uses 5 levels (highest clinical info); others stay at 3 (natural breakpoints).
// 2026-02-16 (Laura): added `hint` — micro-promemoria EMA per ancorare ogni risposta
// alla SITUAZIONE specifica e non allo stato globale del giorno.
const EMA_DIMENSIONS = [
  {
    id: "load",
    question: "Quanto è stata pesante?",
    hint: "Sforzo richiesto qui e ora — fisico o mentale, non la stanchezza accumulata.",
    options: [
      { value: "leggero", label: "Leggera", emoji: "🟢" },
      { value: "medio", label: "Media", emoji: "🟡" },
      { value: "pesante", label: "Pesante", emoji: "🔴" },
    ],
  },
  {
    id: "pleasantness",
    question: "Com'è stata per te?",
    hint: "Quanto è piacevole questa situazione (non il tuo umore in generale).",
    options: [
      { value: "molto_spiacevole", label: "Molto spiacevole", emoji: "🔴" },
      { value: "spiacevole", label: "Spiacevole", emoji: "🟠" },
      { value: "neutra", label: "Neutra", emoji: "🟡" },
      { value: "piacevole", label: "Piacevole", emoji: "🟢" },
      { value: "molto_piacevole", label: "Molto piacevole", emoji: "💚" },
    ],
  },
  {
    id: "voluntariness",
    question: "Quanto l'hai scelta?",
    hint: "Quanto stai facendo questa cosa per scelta tua, in questo momento.",
    options: [
      { value: "per_niente", label: "Per niente", emoji: "🔴" },
      { value: "in_parte", label: "In parte", emoji: "🟡" },
      { value: "totalmente", label: "Totalmente", emoji: "🟢" },
    ],
  },
  {
    id: "control",
    question: "Quanto sentivi di gestirla?",
    hint: "Quanto puoi influenzare quello che sta succedendo adesso.",
    options: [
      { value: "per_niente", label: "Per niente", emoji: "🔴" },
      { value: "in_parte", label: "In parte", emoji: "🟡" },
      { value: "totalmente", label: "Totalmente", emoji: "🟢" },
    ],
  },
  {
    id: "vitality",
    question: "In quel momento ti sentivi:",
    hint: "Energia che senti in questo momento (non come hai dormito).",
    options: [
      { value: "scarica", label: "Scarica", emoji: "🔋" },
      { value: "a_meta", label: "A metà", emoji: "🔋" },
      { value: "carica", label: "Carica", emoji: "⚡" },
    ],
  },
  {
    id: "social_quality",
    question: "Com'è stata l'interazione?",
    hint: "L'atmosfera di questa specifica interazione, non della relazione in generale.",
    hasAbsentToggle: true,
    options: [
      { value: "tesa", label: "Tesa", emoji: "🔴" },
      { value: "neutra", label: "Neutra", emoji: "🟡" },
      { value: "positiva", label: "Positiva", emoji: "🟢" },
    ],
  },
];

// ===== EMA reminding anchors (Tipo A — primi 20 check-in) =====
// 2026-02-16 (Laura): micro-prompt iniziale per ridurre il carry-over con
// l'umore globale e ancorare la valutazione alla situazione specifica.
//
// Mostriamo SEMPRE 2 frasi:
//   - una "principale" in rotazione fra ANCHOR_PHRASES_PRIMARY
//   - una "secondaria" sempre la stessa (il concetto "rimasta addosso" è
//     importante e non deve mai mancare quando la situazione è cambiata da
//     poco). Laura 2026-02-16 v2: confermato che la 2ª frase non deve essere
//     in alternativa ma sempre accanto alla principale.
const ANCHOR_PHRASES_PRIMARY = [
  "Pensa alla situazione di adesso o di pochi minuti fa — è quella che valuti, non l'umore della giornata.",
  "Ogni risposta si riferisce a questo momento specifico, non a come ti senti in generale oggi.",
];
const ANCHOR_PHRASE_CARRY_OVER =
  "Se la situazione è cambiata da pochissimo, valuta quella che ti è \"rimasta addosso\" in questo momento.";

const ANCHOR_INTRO_MAX_CHECKINS = 20;
const CHECKIN_COUNT_KEY = "revela_checkin_count_v1";

const readCheckinCount = () => {
  try {
    const v = parseInt(localStorage.getItem(CHECKIN_COUNT_KEY) || "0", 10);
    return Number.isFinite(v) ? v : 0;
  } catch {
    return 0;
  }
};

const incrementCheckinCount = () => {
  try {
    const next = readCheckinCount() + 1;
    localStorage.setItem(CHECKIN_COUNT_KEY, String(next));
    return next;
  } catch {
    return 0;
  }
};

// ===== STEP HEADER =====
const StepHeader = ({ step, total, title, subtitle, icon: Icon }) => (
  <div className="space-y-2">
    <div className="flex items-center gap-2">
      {[...Array(total)].map((_, i) => (
        <div
          key={`step-indicator-${i}`}
          className={`h-1 flex-1 rounded-full transition-colors ${i < step ? "bg-rose-500" : "bg-muted"}`}
        />
      ))}
    </div>
    <div className="flex items-center gap-2 pt-1">
      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-white" />
      </div>
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wider text-rose-500">Step {step}/{total}</p>
        <h3 className="text-base font-semibold">{title}</h3>
      </div>
    </div>
    {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
  </div>
);

// ===== TILE BUTTON =====
// 2026-05-18: optional `examples` shown on long-press / tap-info chevron, never always visible.
const TileButton = ({ active, onClick, icon: Icon, label, testid, examples }) => {
  const [showEx, setShowEx] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={onClick}
        data-testid={testid}
        className={`w-full flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl border-2 transition-all active:scale-95 ${
          active
            ? "border-rose-500 bg-rose-50 shadow-sm"
            : "border-muted bg-white hover:border-rose-200 hover:bg-rose-50/30"
        }`}
      >
        <Icon className={`w-6 h-6 ${active ? "text-rose-600" : "text-muted-foreground"}`} />
        <span className={`text-xs font-medium text-center leading-tight ${active ? "text-rose-700" : "text-foreground"}`}>
          {label}
        </span>
      </button>
      {examples && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setShowEx((v) => !v); }}
          className="absolute top-1 right-1 w-4 h-4 rounded-full bg-white border border-muted-foreground/20 text-muted-foreground flex items-center justify-center hover:bg-rose-50"
          aria-label="Vedi esempi"
          data-testid={`${testid}-info`}
        >
          {showEx ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
      )}
      {showEx && examples && (
        <div className="absolute z-10 left-0 right-0 top-full mt-1 p-2 rounded-md bg-slate-900 text-white text-[10px] leading-tight shadow-lg">
          {examples}
        </div>
      )}
    </div>
  );
};

// ===== EMA DIMENSION PICKER (2026-05-12) =====
// 3 discrete buttons (no more sliders). Faster to tap, less ambiguous.
// 2026-02-16 (Laura): aggiunto hint sotto la domanda per ancorare la
// valutazione alla SITUAZIONE (non allo stato globale del giorno).
const DimensionPicker = ({ dimension, value, onChange, absent, onAbsentChange }) => {
  return (
    <div className="space-y-2" data-testid={`ema-dim-${dimension.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">{dimension.question}</p>
          {dimension.hint && (
            <p
              className="text-[11px] text-stone-500 italic leading-snug mt-0.5"
              data-testid={`ema-hint-${dimension.id}`}
            >
              {dimension.hint}
            </p>
          )}
        </div>
        {dimension.hasAbsentToggle && (
          <button
            onClick={() => onAbsentChange(!absent)}
            className={`text-[11px] px-2.5 py-1 rounded-md border-2 whitespace-nowrap transition-colors font-medium ${
              absent
                ? "border-rose-500 bg-rose-500 text-white shadow-sm"
                : "border-rose-300 bg-white text-rose-600 hover:bg-rose-50"
            }`}
            data-testid={`ema-${dimension.id}-absent-toggle`}
          >
            {absent ? "✓ Non interagivo" : "Non interagivo"}
          </button>
        )}
      </div>

      {!absent && (
        <div className={`grid gap-1.5 ${dimension.options.length === 5 ? "grid-cols-5" : "grid-cols-3"}`}>
          {dimension.options.map((opt) => {
            const active = value === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => onChange(opt.value)}
                data-testid={`ema-${dimension.id}-${opt.value}`}
                className={`flex flex-col items-center justify-center gap-1 p-2 rounded-xl border-2 transition-all active:scale-95 ${
                  active
                    ? "border-rose-500 bg-rose-50 shadow-sm"
                    : "border-muted bg-white hover:border-rose-200 hover:bg-rose-50/30"
                }`}
              >
                <span className="text-lg leading-none">{opt.emoji}</span>
                <span className={`text-[10px] font-medium leading-tight text-center ${active ? "text-rose-700" : "text-foreground"}`}>
                  {opt.label}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ===== MINI VOICE CLIP RECORDER =====
const MiniClipRecorder = ({ onResult, maxSeconds = 15, placeholder = "Oppure tocca il microfono per dettare" }) => {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const cancelledRef = useRef(false);

  const stop = useCallback(() => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") {
      mediaRef.current.stop();
    }
    clearInterval(timerRef.current);
    setRecording(false);
    playMicStop();
  }, []);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    stop();
    toast("Clip cestinata");
  }, [stop]);

  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm;codecs=opus" });
      mediaRef.current = mr;
      chunksRef.current = [];
      cancelledRef.current = false;

      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        if (cancelledRef.current) return;

        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setProcessing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "clip.webm");
          const res = await api.post("/voice-mood/transcribe-clip", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          onResult(res.data);
        } catch (err) {
          toast.error(err.response?.data?.detail || "Errore nella trascrizione");
        } finally {
          setProcessing(false);
        }
      };

      mr.start(250);
      playMicStart();
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => {
        setElapsed((p) => {
          const next = p + 1;
          if (next >= maxSeconds) stop();
          return next;
        });
      }, 1000);
    } catch {
      toast.error("Impossibile accedere al microfono");
    }
  }, [maxSeconds, onResult, stop]);

  useEffect(() => () => clearInterval(timerRef.current), []);

  if (processing) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground justify-center py-2" data-testid="clip-processing">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span>Trascrivo...</span>
      </div>
    );
  }

  if (recording) {
    return (
      <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-2">
        <button
          onClick={stop}
          className="w-8 h-8 rounded-full bg-red-500 animate-pulse flex items-center justify-center shrink-0"
          data-testid="clip-stop-btn"
          aria-label="Ferma clip"
        >
          <Square className="w-3 h-3 text-white fill-white" />
        </button>
        <div className="flex-1 text-xs text-red-700">
          Tocca il quadrato per inviare · {elapsed}s / {maxSeconds}s
        </div>
        <button
          onClick={cancel}
          className="w-8 h-8 rounded-full bg-white border border-red-200 text-red-500 flex items-center justify-center shrink-0"
          data-testid="clip-cancel-btn"
          aria-label="Cestina clip"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={start}
      className="flex items-center gap-2 w-full p-2 rounded-lg border border-dashed border-rose-300 text-xs text-rose-600 hover:bg-rose-50 transition-colors"
      data-testid="clip-start-btn"
    >
      <Mic className="w-4 h-4" />
      <span>{placeholder}</span>
    </button>
  );
};

// ===== STRUCTURED CHECK-IN WIZARD =====
// 2026-05-21 v2.3: Step 0 (frame chooser) REMOVED — Laura's methodological choice.
// All check-ins now describe "the moment immediately before opening the wizard".
const StructuredCheckIn = ({ onComplete, onCancel, samplingType = null }) => {
  const isSelfInitiated = samplingType === "self_initiated";
  const [step, setStep] = useState(1); // wizard 1..4
  const [saving, setSaving] = useState(false);
  const [pendingPrompt, setPendingPrompt] = useState(null);
  // 2026-02-16 (Laura): counter check-in locale per gestire la frase EMA iniziale
  // (Tipo A, primi 20 check-in). Persistita in localStorage tra reload.
  const [checkinCount] = useState(() => readCheckinCount());
  const anchorPrimary = checkinCount < ANCHOR_INTRO_MAX_CHECKINS
    ? ANCHOR_PHRASES_PRIMARY[checkinCount % ANCHOR_PHRASES_PRIMARY.length]
    : null;
  const anchorCarryOver = checkinCount < ANCHOR_INTRO_MAX_CHECKINS
    ? ANCHOR_PHRASE_CARRY_OVER
    : null;
  // 2026-05-21 v2.3: frame is always "prompt_time" (the moment just before compiling).
  // The variable is kept for backward-compat with the backend response_tier logic.
  const frame = "prompt_time";
  const [effectiveSamplingType, setEffectiveSamplingType] = useState(samplingType);

  // On mount: look up a pending prompt to attach for delay tracking (no frame chooser anymore).
  useEffect(() => {
    if (isSelfInitiated) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/voice-mood/pending-prompt");
        if (cancelled) return;
        if (res.data?.pending) {
          if (!effectiveSamplingType && res.data?.sampling_type) {
            setEffectiveSamplingType(res.data.sampling_type);
          }
          setPendingPrompt(res.data);  // used to attach prompt_sent_at on submit
        }
      } catch {
        /* ignore */
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelfInitiated]);

  // Step 1: location
  const [location, setLocation] = useState(null);
  const [locationDetail, setLocationDetail] = useState("");

  // Step 2: social — flag VIRTUALE in cima (Laura 21/05), "Solo" disabilitato se flag attivo
  const [socialContext, setSocialContext] = useState(null);
  const [socialDetail, setSocialDetail] = useState("");
  const [virtualInteraction, setVirtualInteraction] = useState(false);

  // Step 3: activity
  const [activity, setActivity] = useState(null);
  const [activityDetail, setActivityDetail] = useState("");

  // Step 4: EMA dimensions — 4 items on single scrollable page (v2.3)
  const [loadValue, setLoadValue] = useState(null);
  const [pleasantnessValue, setPleasantnessValue] = useState(null);
  const [voluntarinessValue, setVoluntarinessValue] = useState(null);
  const [controlValue, setControlValue] = useState(null);  // NEW v2.3
  const [vitalityValue, setVitalityValue] = useState(null);  // NEW v2.4 (Russell arousal)
  const [socialQualityValue, setSocialQualityValue] = useState(null);
  const [socialAbsent, setSocialAbsent] = useState(false);
  const [notes, setNotes] = useState("");
  const [causalFactors, setCausalFactors] = useState([]);

  const activityIsOtherNoDetail = activity === "altro" && !activityDetail.trim();

  const socialQualityComplete = socialAbsent || socialQualityValue !== null;

  const canProceed = () => {
    if (step === 1) return !!location;
    if (step === 2) return !!socialContext;
    if (step === 3) return !!activity && !activityIsOtherNoDetail;
    if (step === 4) {
      return (
        loadValue !== null &&
        pleasantnessValue !== null &&
        voluntarinessValue !== null &&
        controlValue !== null &&  // 2026-05-21 v2.3: control is required
        vitalityValue !== null &&  // 2026-05-22 v2.4: vitality is required
        socialQualityComplete
      );
    }
    return false;
  };

  // 2026-05-21 v2.3: auto-advance after a brief delay when user picks a value on steps 1-3.
  // Step 4 stays manual (multi-item single-page layout).
  useEffect(() => {
    if (step === 1 && location) {
      const t = setTimeout(() => setStep(2), 420);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);
  useEffect(() => {
    // Auto-advance step 2 only when NOT virtual (because virtual ⇒ user may still need to pick a person)
    if (step === 2 && socialContext) {
      const t = setTimeout(() => setStep(3), 420);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socialContext]);
  useEffect(() => {
    if (step === 3 && activity && !activityIsOtherNoDetail && activity !== "altro") {
      const t = setTimeout(() => setStep(4), 420);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity]);

  const handleNext = () => {
    if (!canProceed()) return;
    setStep((s) => Math.min(s + 1, 4));
  };

  const handleBack = () => {
    if (step === 1) {
      onCancel?.();
      return;
    }
    setStep((s) => Math.max(s - 1, 1));
  };

  const handleSubmit = async () => {
    if (!canProceed()) return;
    setSaving(true);
    try {
      const payload = {
        location,
        location_detail: locationDetail || null,
        social_context: socialContext,
        social_detail: socialDetail || null,
        virtual_interaction: virtualInteraction,
        activity,
        activity_detail: activityDetail || null,
        // 2026-05-12: values are already the canonical enum strings (no slider mapping)
        load: loadValue,
        pleasantness: pleasantnessValue,
        voluntariness: voluntarinessValue,
        control: controlValue,  // 2026-05-21 v2.3: NEW (sense of mastery within the situation)
        vitality: vitalityValue,  // 2026-05-22 v2.4: NEW (arousal — Russell Circumplex 2nd axis)
        social_quality: socialAbsent ? "assente" : socialQualityValue,
        notes: notes || null,
        causal_factors: causalFactors,
        // 2026-05-13: temporal frame + optional explicit prompt_sent_at
        frame: frame,
        prompt_sent_at: frame === "prompt_time" && pendingPrompt?.sent_at ? pendingPrompt.sent_at : null,
        // 2026-05-13: explicit EMA sampling type (random | scheduled | self_initiated).
        sampling_type: effectiveSamplingType || undefined,
      };
      const res = await api.post("/voice-mood/structured", payload);
      playSaveChime();
      // 2026-02-16 (Laura): incrementa counter check-in per gestire le frasi EMA iniziali.
      incrementCheckinCount();
      const tier = res.data?.response_tier;
      if (tier === "delayed") {
        toast.success("Salvato ✓ (risposta differita)");
      } else if (tier === "retrospective") {
        toast.success("Salvato ✓ (retrospettiva)");
      } else {
        toast.success("Experience tracking salvato ✓");
      }
      onComplete?.(res.data);
    } catch (err) {
      playError();
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleClipResult = (result) => {
    if (step === 3 && result.transcript) {
      setActivityDetail(result.transcript);
    } else if (step === 4) {
      if (result.transcript) setNotes(result.transcript);
      if (Array.isArray(result.causal_factors) && result.causal_factors.length > 0) {
        setCausalFactors(result.causal_factors);
      }
    }
  };

  return (
    <Card className="border-2 border-rose-200" data-testid="structured-checkin">
      <CardContent className="p-4 space-y-4">
        {/* 2026-02-16 (Laura): EMA anchor phrase — primi 20 check-in.
            Riduce il carry-over con l'umore globale e ancora la valutazione alla
            situazione specifica. Mostriamo SEMPRE 2 messaggi: uno primario in
            rotazione + il concetto "rimasta addosso" che non deve mai mancare. */}
        {anchorPrimary && (
          <div
            className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 space-y-1.5"
            data-testid="ema-anchor-phrase"
          >
            <p className="text-[11.5px] text-amber-900 leading-snug italic">
              <span className="font-semibold not-italic mr-1">Promemoria:</span>
              {anchorPrimary}
            </p>
            {anchorCarryOver && (
              <p
                className="text-[11px] text-amber-800/80 leading-snug italic pl-1 border-l-2 border-amber-300/70"
                data-testid="ema-anchor-carry-over"
              >
                {anchorCarryOver}
              </p>
            )}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-3">
            <StepHeader step={1} total={4} title="Dov'eri appena prima?" subtitle="Descrivi il momento immediatamente precedente all'apertura del check-in" icon={MapPin} />
            <div className="grid grid-cols-2 gap-2">
              {LOCATIONS.map((l) => (
                <TileButton key={l.id} active={location === l.id} onClick={() => setLocation(l.id)} icon={l.icon} label={l.label} testid={`location-${l.id}`} examples={l.examples} />
              ))}
            </div>
            <Input
              placeholder="Luogo specifico (opzionale): es. 'in cucina', 'ufficio casa'"
              value={locationDetail}
              onChange={(e) => setLocationDetail(e.target.value)}
              maxLength={80}
              className="text-sm"
              data-testid="location-detail-input"
            />
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <StepHeader step={2} total={4} title="Con chi eri?" subtitle="Indica chi era in interazione con te" icon={Users} />

            {/* Virtual interaction toggle — IN CIMA (Laura 21/05): se attivo disabilita "Solo" */}
            <button
              onClick={() => {
                const next = !virtualInteraction;
                setVirtualInteraction(next);
                // se attivo flag e avevo selezionato "Solo", lo resetto (incompatibile)
                if (next && socialContext === "solo") setSocialContext(null);
              }}
              className={`w-full flex items-center justify-start gap-2.5 p-3 rounded-lg border-2 transition-colors text-left text-sm ${
                virtualInteraction
                  ? "border-rose-500 bg-rose-50 text-rose-700"
                  : "border-muted bg-white hover:border-rose-200"
              }`}
              data-testid="social-virtual-interaction-toggle"
            >
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                virtualInteraction ? "border-rose-500 bg-rose-500" : "border-muted-foreground/40"
              }`}>
                {virtualInteraction && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
              </div>
              <span className="leading-tight">L&apos;interazione era online / al telefono</span>
            </button>

            <p className="text-[11px] text-muted-foreground italic" data-testid="social-glossa">
              Anche se a distanza. {virtualInteraction && "(\"Solo\" disabilitato perché c'è interazione virtuale)"}
            </p>

            <div className="grid grid-cols-2 gap-2">
              {SOCIAL.map((s) => {
                const disabled = virtualInteraction && s.id === "solo";
                return (
                  <div key={s.id} className={disabled ? "opacity-60 pointer-events-none relative" : "relative"}>
                    <TileButton
                      active={socialContext === s.id}
                      onClick={() => { if (!disabled) setSocialContext(s.id); }}
                      icon={s.icon}
                      label={s.label}
                      testid={`social-${s.id}`}
                      examples={s.examples}
                    />
                    {disabled && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <span className="text-[9px] font-semibold uppercase tracking-wider bg-slate-700 text-white px-2 py-0.5 rounded">
                          n/d
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {socialContext && socialContext !== "solo" && (
              <Input
                placeholder="Con chi nello specifico? (opzionale)"
                value={socialDetail}
                onChange={(e) => setSocialDetail(e.target.value)}
                maxLength={80}
                className="text-sm"
                data-testid="social-detail-input"
              />
            )}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3">
            <StepHeader step={3} total={4} title="Qual è stata l'attività principale?" subtitle="Scegli la categoria principale" icon={ActivityIcon} />
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {ACTIVITIES.map((a) => (
                <TileButton key={a.id} active={activity === a.id} onClick={() => setActivity(a.id)} icon={a.icon} label={a.label} testid={`activity-${a.id}`} examples={a.examples} />
              ))}
            </div>
            <Input
              placeholder={activity === "altro" ? "Specifica cosa stavi facendo (obbligatorio)" : "Dettaglio (opzionale): es. 'riunione cliente'"}
              value={activityDetail}
              onChange={(e) => setActivityDetail(e.target.value)}
              maxLength={120}
              className={`text-sm ${activity === "altro" && !activityDetail.trim() ? "border-amber-400" : ""}`}
              data-testid="activity-detail-input"
            />
            {activityIsOtherNoDetail && (
              <div className="flex items-center gap-1.5 text-[11px] text-amber-700">
                <AlertCircle className="w-3.5 h-3.5" />
                <span>Scrivi almeno cosa stai facendo</span>
              </div>
            )}
            <MiniClipRecorder onResult={handleClipResult} maxSeconds={10} placeholder="Se vuoi, descrivi brevemente la situazione" />
          </div>
        )}

        {step === 4 && (
          <div className="space-y-5">
            <StepHeader step={4} total={4} title="Come hai vissuto questa situazione?" subtitle="Tocca un'opzione per ciascuna domanda" icon={Gauge} />

            <DimensionPicker dimension={EMA_DIMENSIONS[0]} value={loadValue} onChange={setLoadValue} />
            <DimensionPicker dimension={EMA_DIMENSIONS[1]} value={pleasantnessValue} onChange={setPleasantnessValue} />
            <DimensionPicker dimension={EMA_DIMENSIONS[2]} value={voluntarinessValue} onChange={setVoluntarinessValue} />
            <DimensionPicker dimension={EMA_DIMENSIONS[3]} value={controlValue} onChange={setControlValue} />
            <DimensionPicker dimension={EMA_DIMENSIONS[4]} value={vitalityValue} onChange={setVitalityValue} />
            <DimensionPicker
              dimension={EMA_DIMENSIONS[5]}
              value={socialQualityValue}
              onChange={setSocialQualityValue}
              absent={socialAbsent}
              onAbsentChange={setSocialAbsent}
            />

            <div className="pt-2 space-y-2 border-t">
              <p className="text-xs text-muted-foreground">Vuoi aggiungere una nota? (opzionale)</p>
              <MiniClipRecorder onResult={handleClipResult} maxSeconds={15} placeholder="Se vuoi, descrivi brevemente la situazione" />
              <Input
                placeholder="Oppure scrivi qui..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={300}
                className="text-sm"
                data-testid="notes-input"
              />
              {causalFactors.length > 0 && (
                <div className="flex flex-wrap gap-1" data-testid="causal-factors-preview">
                  {causalFactors.map((cf, i) => (
                    <Badge
                      key={`cf-${cf.factor}-${i}`}
                      variant={cf.valence === "positivo" ? "outline" : "destructive"}
                      className="text-[10px]"
                    >
                      {cf.valence === "positivo" ? "+" : "−"} {cf.factor}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex gap-2 pt-2 border-t">
          <Button variant="outline" onClick={handleBack} disabled={saving} className="flex-1" data-testid="wizard-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" />
            {step === 1 ? "Annulla" : "Indietro"}
          </Button>
          {step < 4 ? (
            <Button onClick={handleNext} disabled={!canProceed()} className="flex-1 bg-rose-500 hover:bg-rose-600" data-testid="wizard-next-btn">
              Avanti
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={saving || !canProceed()} className="flex-1 bg-rose-500 hover:bg-rose-600" data-testid="wizard-submit-btn">
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Salvo...</>
              ) : (
                <><Check className="w-4 h-4 mr-1" />Salva check-in</>
              )}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default StructuredCheckIn;
